import {
  BeneficiaryCumul,
  CatalogProduct,
  CultureSubsidySettings,
  PocketBalances,
  ReserveMovement,
  SpecialOperation,
  SubsidyAlert,
  SupervisedCaretaker
} from '../types'
import { fallbackCapacity, isOperationActive } from '../utils/calculator'

export interface MockDatabase {
  version: number
  currentCaretakerId: string
  settings: CultureSubsidySettings[]
  pockets: Record<string, PocketBalances>
  products: CatalogProduct[]
  operations: SpecialOperation[]
  movements: ReserveMovement[]
  beneficiaries: BeneficiaryCumul[]
}

const YEAR = 2026

export const CURRENT_CARETAKER_ID = 'caretaker-horizon'

function isoDaysFromNow(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

function isoHoursAgo(hours: number): string {
  const date = new Date()
  date.setHours(date.getHours() - hours)
  return date.toISOString()
}

export function createSeedDatabase(): MockDatabase {
  const products = createCatalog()
  const settings = createSettings()
  const pockets = createPockets()
  const operations = createOperations()
  const movements = createMovements()
  const beneficiaries = createBeneficiaries()

  return {
    version: 2,
    currentCaretakerId: CURRENT_CARETAKER_ID,
    settings,
    pockets,
    products,
    operations,
    movements,
    beneficiaries
  }
}

export function buildRuntimeAlerts(db: MockDatabase): SubsidyAlert[] {
  const alerts: SubsidyAlert[] = []
  const now = new Date()

  db.settings.forEach(item => {
    const beneficiaries = db.beneficiaries.filter(row => row.caretakerId === item.caretakerId)
    const nearCap = beneficiaries.filter(row => row.consumed / item.annualBeneficiaryCapping >= 0.8).length
    const atCap = beneficiaries.filter(row => row.consumed >= item.annualBeneficiaryCapping).length
    const ops = db.operations.filter(operation => operation.caretakerId === item.caretakerId)
    const activeOps = ops.filter(operation => isOperationActive(operation, now))
    const hasGlobal = activeOps.some(operation => operation.type === 'global')
    const hasProduct = activeOps.some(operation => operation.type === 'per_product')

    if (item.status === 'blocked') {
      alerts.push({
        id: `alert-blocked-${item.caretakerId}`,
        severity: 'critical',
        title: 'Subvention bloquée',
        message: item.blockedReason || 'La subvention culture est bloquée.',
        caretakerId: item.caretakerId,
        caretakerName: item.caretakerName,
        createdAt: isoHoursAgo(4)
      })
    }

    if (item.currentReserve <= 0 && item.status === 'active') {
      alerts.push({
        id: `alert-empty-${item.caretakerId}`,
        severity: 'critical',
        title: 'Réserve à zéro',
        message: 'La réserve culture est vide. Les achats s’appuieront uniquement sur le fallback, s’il est actif.',
        caretakerId: item.caretakerId,
        caretakerName: item.caretakerName,
        createdAt: isoHoursAgo(6)
      })
    } else if (item.parameterizedBudget > 0 && item.currentReserve / item.parameterizedBudget < 0.15 && item.status === 'active') {
      alerts.push({
        id: `alert-low-${item.caretakerId}`,
        severity: 'watch',
        title: 'Réserve faible',
        message: `Il reste ${formatPlain(item.currentReserve)} € en réserve, soit moins de 15 % du budget paramétré.`,
        caretakerId: item.caretakerId,
        caretakerName: item.caretakerName,
        createdAt: isoHoursAgo(12)
      })
    }

    if (item.parameterizedBudget > 0 && item.annualConsumed / item.parameterizedBudget >= 0.8) {
      alerts.push({
        id: `alert-budget-${item.caretakerId}`,
        severity: 'watch',
        title: 'Budget largement consommé',
        message: `${Math.round((item.annualConsumed / item.parameterizedBudget) * 100)} % du budget paramétré a déjà été consommé cette année.`,
        caretakerId: item.caretakerId,
        caretakerName: item.caretakerName,
        createdAt: isoHoursAgo(20)
      })
    }

    if (nearCap > 0) {
      const capCopy = annualCapAlertCopy(nearCap, atCap)
      alerts.push({
        id: `alert-cap-${item.caretakerId}`,
        severity: nearCap >= 3 ? 'watch' : 'info',
        title: capCopy.title,
        message: capCopy.message,
        caretakerId: item.caretakerId,
        caretakerName: item.caretakerName,
        createdAt: isoHoursAgo(28)
      })
    }

    if (hasGlobal && hasProduct) {
      alerts.push({
        id: `alert-ops-${item.caretakerId}`,
        severity: 'info',
        title: 'Opérations spéciales superposées',
        message: 'Une opération globale coexiste avec une opération par produit. Le paramétrage le plus spécifique prime — ce n’est pas bloquant.',
        caretakerId: item.caretakerId,
        caretakerName: item.caretakerName,
        createdAt: isoHoursAgo(40)
      })
    }

    if (item.perProductValueMode) {
      const missing = db.products.filter(product => product.eligible && product.valuePerProduct == null).length
      if (missing > 0) {
        alerts.push({
          id: `alert-values-${item.caretakerId}`,
          severity: 'info',
          title: 'Mode valeur incomplet',
          message: `${missing} produit${missing > 1 ? 's éligibles n’ont' : ' éligible n’a'} pas de valeur individuelle. Le taux standard s’applique pour eux.`,
          caretakerId: item.caretakerId,
          caretakerName: item.caretakerName,
          createdAt: isoHoursAgo(50)
        })
      }
    }
  })

  return alerts.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export function toSupervisedCaretaker(db: MockDatabase, settings: CultureSubsidySettings): SupervisedCaretaker {
  const beneficiaries = db.beneficiaries.filter(row => row.caretakerId === settings.caretakerId)
  const movements = db.movements.filter(row => row.caretakerId === settings.caretakerId)
  const operations = db.operations.filter(row => row.caretakerId === settings.caretakerId)
  const pockets = db.pockets[settings.caretakerId]

  return {
    id: settings.caretakerId,
    name: settings.caretakerName,
    status: settings.status,
    parameterizedBudget: settings.parameterizedBudget,
    currentReserve: settings.currentReserve,
    annualConsumed: settings.annualConsumed,
    beneficiariesCount: beneficiaries.length,
    beneficiariesAtCap: beneficiaries.filter(row => row.consumed >= settings.annualBeneficiaryCapping).length,
    beneficiariesNearCap: beneficiaries.filter(row => row.consumed / settings.annualBeneficiaryCapping >= 0.8).length,
    activeOperationsCount: operations.filter(operation => isOperationActive(operation)).length,
    lastMovementAt: movements[0]?.date || null,
    fallbackCapacity: pockets ? fallbackCapacity(settings, pockets) : 0
  }
}

function createCatalog(): CatalogProduct[] {
  return [
    product('prod-gaumont-1', 'Gaumont', '#0B1F4B', 'Place cinéma Gaumont', 14.9, 11.9, true, null),
    product('prod-gaumont-5', 'Gaumont', '#0B1F4B', 'Carnet 5 places cinéma', 62, 49.9, true, 15),
    product('prod-cgr-1', 'CGR', '#C8102E', 'Place cinéma CGR', 12.5, 9.9, true, null),
    product('prod-fnac-20', 'Fnac', '#E9C400', 'Carte cadeau Fnac 20 €', 20, 20, true, 6),
    product('prod-fnac-50', 'Fnac', '#E9C400', 'Carte cadeau Fnac 50 €', 50, 50, true, 15),
    product('prod-fnac-concert', 'Fnac Spectacles', '#E9C400', 'Place de concert', 45, 39, true, null),
    product('prod-dlp-1', 'Disneyland Paris', '#006EC7', 'Billet 1 jour Disneyland Paris', 99, 89, true, 25),
    product('prod-dlp-2', 'Disneyland Paris', '#006EC7', 'Billet 2 jours Disneyland Paris', 179, 159, true, 40),
    product('prod-dlp-3', 'Disneyland Paris', '#006EC7', 'Billet 3 jours Disneyland Paris', 229, 199, true, null),
    product('prod-cultura-30', 'Cultura', '#7A1FA2', 'Carte Cultura 30 €', 30, 30, true, 8),
    product('prod-orsay', 'Musée d’Orsay', '#8B1E1E', 'Billet Musée d’Orsay', 16, 14, true, null),
    product('prod-pompidou', 'Centre Pompidou', '#111111', 'Billet Centre Pompidou', 15, 13, false, null)
  ]
}

function product(
  id: string,
  brand: string,
  brandColor: string,
  name: string,
  publicPrice: number,
  csePrice: number,
  eligible: boolean,
  valuePerProduct: number | null
): CatalogProduct {
  return { id, brand, brandColor, name, publicPrice, csePrice, eligible, valuePerProduct }
}

function createSettings(): CultureSubsidySettings[] {
  return [
    {
      id: 'sub-horizon',
      caretakerId: CURRENT_CARETAKER_ID,
      caretakerName: 'CSE Horizon',
      status: 'active',
      blockedReason: null,
      standardRate: 0.3,
      perProductValueMode: true,
      orderCapping: 40,
      annualBeneficiaryCapping: 150,
      parameterizedBudget: 50000,
      currentReserve: 12500,
      annualConsumed: 18740,
      fallbackLoyalty: true,
      fallbackFree: true,
      fallbackRecycling: false,
      wireReference: 'CULTURE-HORIZON-2026',
      updatedAt: isoHoursAgo(8)
    },
    {
      id: 'sub-atlas',
      caretakerId: 'caretaker-atlas',
      caretakerName: 'CSE Atlas',
      status: 'blocked',
      blockedReason: 'Réserve épuisée et fallbacks inactifs.',
      standardRate: 0.25,
      perProductValueMode: false,
      orderCapping: 30,
      annualBeneficiaryCapping: 120,
      parameterizedBudget: 20000,
      currentReserve: 0,
      annualConsumed: 19820,
      fallbackLoyalty: false,
      fallbackFree: false,
      fallbackRecycling: false,
      wireReference: 'CULTURE-ATLAS-2026',
      updatedAt: isoHoursAgo(30)
    },
    {
      id: 'sub-lyon',
      caretakerId: 'caretaker-lyon',
      caretakerName: 'Mairie de Lyon',
      status: 'active',
      blockedReason: null,
      standardRate: 0.4,
      perProductValueMode: false,
      orderCapping: 50,
      annualBeneficiaryCapping: 200,
      parameterizedBudget: 80000,
      currentReserve: 22100,
      annualConsumed: 41200,
      fallbackLoyalty: true,
      fallbackFree: true,
      fallbackRecycling: true,
      wireReference: 'CULTURE-LYON-2026',
      updatedAt: isoHoursAgo(2)
    },
    {
      id: 'sub-sncf',
      caretakerId: 'caretaker-sncf',
      caretakerName: 'Comité SNCF Ouest',
      status: 'disabled',
      blockedReason: null,
      standardRate: 0.2,
      perProductValueMode: false,
      orderCapping: 25,
      annualBeneficiaryCapping: 100,
      parameterizedBudget: 15000,
      currentReserve: 4300,
      annualConsumed: 0,
      fallbackLoyalty: false,
      fallbackFree: true,
      fallbackRecycling: false,
      wireReference: 'CULTURE-SNCF-OUEST-2026',
      updatedAt: isoHoursAgo(80)
    },
    {
      id: 'sub-harmonie',
      caretakerId: 'caretaker-harmonie',
      caretakerName: 'CSE Harmonie',
      status: 'active',
      blockedReason: null,
      standardRate: 0.35,
      perProductValueMode: false,
      orderCapping: 45,
      annualBeneficiaryCapping: 180,
      parameterizedBudget: 35000,
      currentReserve: 2800,
      annualConsumed: 9100,
      fallbackLoyalty: true,
      fallbackFree: false,
      fallbackRecycling: false,
      wireReference: 'CULTURE-HARMONIE-2026',
      updatedAt: isoHoursAgo(16)
    }
  ]
}

function createPockets(): Record<string, PocketBalances> {
  return {
    [CURRENT_CARETAKER_ID]: { free: 45200, loyalty: 8200, recycling: 1500, cultureReserve: 12500 },
    'caretaker-atlas': { free: 6100, loyalty: 0, recycling: 400, cultureReserve: 0 },
    'caretaker-lyon': { free: 98000, loyalty: 12400, recycling: 3200, cultureReserve: 22100 },
    'caretaker-sncf': { free: 18700, loyalty: 2100, recycling: 0, cultureReserve: 4300 },
    'caretaker-harmonie': { free: 22300, loyalty: 5400, recycling: 800, cultureReserve: 2800 }
  }
}

function createOperations(): SpecialOperation[] {
  return [
    {
      id: 'op-culture-week',
      caretakerId: CURRENT_CARETAKER_ID,
      type: 'global',
      productId: null,
      productName: null,
      startDate: isoDaysFromNow(-4),
      endDate: isoDaysFromNow(10),
      boostedRate: 0.4,
      boostedValue: null,
      name: 'Semaine de la culture'
    },
    {
      id: 'op-dlp',
      caretakerId: CURRENT_CARETAKER_ID,
      type: 'per_product',
      productId: 'prod-dlp-1',
      productName: 'Billet 1 jour Disneyland Paris',
      startDate: isoDaysFromNow(-2),
      endDate: isoDaysFromNow(20),
      boostedRate: 0.5,
      boostedValue: null,
      name: 'Disneyland à -50 %'
    },
    {
      id: 'op-rentree',
      caretakerId: CURRENT_CARETAKER_ID,
      type: 'global',
      productId: null,
      productName: null,
      startDate: isoDaysFromNow(25),
      endDate: isoDaysFromNow(40),
      boostedRate: 0.35,
      boostedValue: null,
      name: 'Rentrée culturelle'
    },
    {
      id: 'op-lyon-festival',
      caretakerId: 'caretaker-lyon',
      type: 'global',
      productId: null,
      productName: null,
      startDate: isoDaysFromNow(-1),
      endDate: isoDaysFromNow(14),
      boostedRate: 0.5,
      boostedValue: null,
      name: 'Nuits de Fourvière'
    }
  ]
}

function createMovements(): ReserveMovement[] {
  return [
    movement('m1', CURRENT_CARETAKER_ID, 'debit_cashback', -24, 'culture_reserve', 'CMD-24091', 'Camille Renard', isoHoursAgo(5), 'Place cinéma Gaumont'),
    movement('m2', CURRENT_CARETAKER_ID, 'debit_cashback', -40, 'culture_reserve', 'CMD-24088', 'Noah Benali', isoHoursAgo(18), 'Carte Fnac 50 €'),
    movement('m3', CURRENT_CARETAKER_ID, 'feed_from_free', 5000, 'culture_reserve', null, null, isoHoursAgo(30), 'Transfert manuel depuis l’acompte libre'),
    movement('m4', CURRENT_CARETAKER_ID, 'debit_cashback', -25, 'loyalty', 'CMD-24071', 'Léa Moreau', isoHoursAgo(46), 'Fallback fidélité — billet Orsay'),
    movement('m5', CURRENT_CARETAKER_ID, 'feed_wire', 8000, 'culture_reserve', null, null, isoHoursAgo(90), 'Virement CULTURE-HORIZON-2026'),
    movement('m6', CURRENT_CARETAKER_ID, 'debit_cashback', -40, 'culture_reserve', 'CMD-24012', 'Hugo Martins', isoHoursAgo(120), 'Billet Disneyland 1 jour'),
    movement('m7', CURRENT_CARETAKER_ID, 'cancel_cashback', 24, 'culture_reserve', 'CMD-23990', 'Inès Diallo', isoHoursAgo(150), 'Annulation commande'),
    movement('m8', CURRENT_CARETAKER_ID, 'feed_from_loyalty', 2000, 'culture_reserve', null, null, isoHoursAgo(200), 'Transfert depuis la fidélité'),
    movement('m9', CURRENT_CARETAKER_ID, 'empty_to_free', -1500, 'free', null, null, isoHoursAgo(260), 'Vidage partiel vers l’acompte libre'),
    movement('m10', CURRENT_CARETAKER_ID, 'debit_cashback', -18, 'culture_reserve', 'CMD-23801', 'Sarah Cohen', isoHoursAgo(300), 'Place CGR'),
    movement('m11', 'caretaker-lyon', 'feed_wire', 15000, 'culture_reserve', null, null, isoHoursAgo(10), 'Virement CULTURE-LYON-2026'),
    movement('m12', 'caretaker-atlas', 'debit_cashback', -30, 'culture_reserve', 'CMD-AT-102', 'Paul Rivière', isoHoursAgo(40), 'Dernier achat avant blocage')
  ].sort((a, b) => b.date.localeCompare(a.date))
}

function movement(
  id: string,
  caretakerId: string,
  type: ReserveMovement['type'],
  amount: number,
  pocket: ReserveMovement['pocket'],
  orderRef: string | null,
  beneficiaryName: string | null,
  date: string,
  comment: string | null
): ReserveMovement {
  return { id, caretakerId, type, amount, pocket, orderRef, beneficiaryName, date, comment }
}

function createBeneficiaries(): BeneficiaryCumul[] {
  return [
    beneficiary('b1', CURRENT_CARETAKER_ID, 'Camille', 'Renard', 'camille.renard@horizon.test', 96, isoHoursAgo(5)),
    beneficiary('b2', CURRENT_CARETAKER_ID, 'Noah', 'Benali', 'noah.benali@horizon.test', 150, isoHoursAgo(18)),
    beneficiary('b3', CURRENT_CARETAKER_ID, 'Léa', 'Moreau', 'lea.moreau@horizon.test', 128, isoHoursAgo(46)),
    beneficiary('b4', CURRENT_CARETAKER_ID, 'Hugo', 'Martins', 'hugo.martins@horizon.test', 40, isoHoursAgo(120)),
    beneficiary('b5', CURRENT_CARETAKER_ID, 'Inès', 'Diallo', 'ines.diallo@horizon.test', 12, isoHoursAgo(150)),
    beneficiary('b6', CURRENT_CARETAKER_ID, 'Sarah', 'Cohen', 'sarah.cohen@horizon.test', 84, isoHoursAgo(300)),
    beneficiary('b7', CURRENT_CARETAKER_ID, 'Yanis', 'Petit', 'yanis.petit@horizon.test', 0, null),
    beneficiary('b8', CURRENT_CARETAKER_ID, 'Chloé', 'Bernard', 'chloe.bernard@horizon.test', 61, isoHoursAgo(400)),
    beneficiary('b9', CURRENT_CARETAKER_ID, 'Adam', 'Khelifi', 'adam.khelifi@horizon.test', 141, isoHoursAgo(70)),
    beneficiary('b10', CURRENT_CARETAKER_ID, 'Emma', 'Leroy', 'emma.leroy@horizon.test', 33, isoHoursAgo(210)),
    beneficiary('b11', 'caretaker-lyon', 'Julien', 'Faure', 'julien.faure@lyon.test', 180, isoHoursAgo(8)),
    beneficiary('b12', 'caretaker-atlas', 'Paul', 'Rivière', 'paul.riviere@atlas.test', 120, isoHoursAgo(40))
  ]
}

function beneficiary(
  id: string,
  caretakerId: string,
  firstName: string,
  lastName: string,
  email: string,
  consumed: number,
  lastPurchaseAt: string | null
): BeneficiaryCumul {
  return { id, caretakerId, firstName, lastName, email, year: YEAR, consumed, lastPurchaseAt }
}

function formatPlain(value: number): string {
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 0 })
}

function annualCapAlertCopy(nearCap: number, atCap: number): { title: string; message: string } {
  const stillNear = nearCap - atCap

  if (atCap > 0 && stillNear === 0) {
    return {
      title: atCap === 1 ? '1 bénéficiaire a atteint le plafond' : `${atCap} bénéficiaires ont atteint le plafond`,
      message: atCap === 1
        ? 'Son enveloppe annuelle est épuisée : plus de subvention pour cette personne cette année.'
        : 'Leur enveloppe annuelle est épuisée : plus de subvention pour ces personnes cette année.'
    }
  }

  const title = nearCap === 1
    ? '1 bénéficiaire proche du plafond'
    : `${nearCap} bénéficiaires proches du plafond`

  if (atCap === 0) {
    return {
      title,
      message: nearCap === 1
        ? 'Cette personne a déjà utilisé plus de 80 % de son enveloppe annuelle.'
        : 'Ils ont déjà utilisé plus de 80 % de leur enveloppe annuelle.'
    }
  }

  const atCapPart = atCap === 1 ? '1 a atteint le plafond.' : `${atCap} ont atteint le plafond.`
  const nearPart = stillNear === 1
    ? '1 autre a dépassé 80 % de son enveloppe annuelle.'
    : `${stillNear} autres ont dépassé 80 % de leur enveloppe annuelle.`

  return { title, message: `${atCapPart} ${nearPart}` }
}
